/**
 * 礼品服务
 * 处理赠送权益相关的API调用
 */
import { post, ApiResponse } from '@/utils/request';

/**
 * 礼品领取状态枚举
 */
export enum GiftStatus {
  /** 待审核 */
  PENDING = 0,
  /** 驳回 */
  REJECTED = 1,
  /** 通过未核销 */
  APPROVED_UNCLAIMED = 2,
  /** 已核销 */
  CLAIMED = 3,
  /** 已过期 */
  EXPIRED = 4,
}

/**
 * 礼品领取请求参数
 */
export interface ClaimGiftParams {
  /** 礼品ID */
  gift_id: string;
  /** 被邀请人用户ID（当前登录用户） */
  invitee_user_id: string;
}

/**
 * 礼品领取响应数据
 */
export interface ClaimGiftData {
  /** 礼品ID */
  gift_id: string;
  /** 状态：0-待审核, 1-驳回, 2-通过未核销, 3-已核销, 4-已过期 */
  status: GiftStatus;
}

/**
 * 礼品服务类
 */
class GiftService {
  /**
   * 领取权益/核销礼品
   * @param params 领取参数
   * @returns 领取结果
   */
  async claimGift(params: ClaimGiftParams): Promise<ApiResponse<ClaimGiftData>> {
    return post('/gift/claimgift', params);
  }

  /**
   * 获取礼品状态描述
   * @param status 状态码
   * @returns 状态描述
   */
  getStatusDescription(status: GiftStatus): string {
    const statusMap: Record<GiftStatus, string> = {
      [GiftStatus.PENDING]: '待审核',
      [GiftStatus.REJECTED]: '已驳回',
      [GiftStatus.APPROVED_UNCLAIMED]: '可领取',
      [GiftStatus.CLAIMED]: '该邀请已被使用',
      [GiftStatus.EXPIRED]: '该邀请已过期',
    };
    return statusMap[status] || '未知状态';
  }

  /**
   * 判断礼品是否可以领取
   * @param status 状态码
   * @returns 是否可领取
   */
  isClaimable(status: GiftStatus): boolean {
    return status === GiftStatus.APPROVED_UNCLAIMED;
  }

  /**
   * 判断是否为错误状态（已核销或已过期）
   * @param status 状态码
   * @returns 是否为错误状态
   */
  isErrorStatus(status: GiftStatus): boolean {
    return status === GiftStatus.CLAIMED || status === GiftStatus.EXPIRED;
  }
}

export default new GiftService();
